import { useMemo } from 'react';
import type { Canvas } from '../types';

interface PixelCanvasProps {
  canvas: Canvas | null;
  highlightPixels?: Array<{ x: number; y: number; color: string }>;
  onPixelClick?: (x: number, y: number) => void;
  onPixelHover?: (x: number | null, y: number | null) => void;
  pixelSize?: number;
  canvasOpacity?: number;
}

export function PixelCanvas({ 
  canvas, 
  highlightPixels = [], 
  onPixelClick,
  onPixelHover,
  pixelSize = 5,
  canvasOpacity = 1.0
}: PixelCanvasProps) {
  const canvasSize = 100 * pixelSize;

  const highlightMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const pixel of highlightPixels) {
      map.set(`${pixel.x},${pixel.y}`, pixel.color);
    }
    return map;
  }, [highlightPixels]);

  const getPixelCoords = (clientX: number, clientY: number, rect: DOMRect) => {
    const x = Math.floor((clientX - rect.left) / pixelSize);
    const y = Math.floor((clientY - rect.top) / pixelSize);
    
    if (x >= 0 && x < 100 && y >= 0 && y < 100) {
      return { x, y };
    }
    return null;
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onPixelClick) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const coords = getPixelCoords(e.clientX, e.clientY, rect);
    
    if (coords) {
      onPixelClick(coords.x, coords.y);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!onPixelClick || e.touches.length > 0) return;

    // Only register as click if it was a single tap (no multi-touch)
    const touch = e.changedTouches[0];
    if (!touch) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const coords = getPixelCoords(touch.clientX, touch.clientY, rect);
    
    if (coords) {
      onPixelClick(coords.x, coords.y);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onPixelHover) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const coords = getPixelCoords(e.clientX, e.clientY, rect);

    if (coords) {
      onPixelHover(coords.x, coords.y);
    } else {
      onPixelHover(null, null);
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!onPixelHover || e.touches.length !== 1) return;

    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const coords = getPixelCoords(touch.clientX, touch.clientY, rect);

    if (coords) {
      onPixelHover(coords.x, coords.y);
    } else {
      onPixelHover(null, null);
    }
  };

  const handleMouseLeave = () => {
    if (onPixelHover) {
      onPixelHover(null, null);
    }
  };

  const handleTouchCancel = () => {
    if (onPixelHover) {
      onPixelHover(null, null);
    }
  };

  if (!canvas) {
    return (
      <div 
        className="border border-gray-300 bg-gray-100 flex items-center justify-center"
        style={{ width: canvasSize, height: canvasSize }}
      >
        <p className="text-gray-500">Canvas lädt...</p>
      </div>
    );
  }

  return (
    <div 
      className="border border-gray-300 relative cursor-crosshair touch-none"
      style={{ width: canvasSize, height: canvasSize }}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onTouchCancel={handleTouchCancel}
    >
      <svg width={canvasSize} height={canvasSize}>
        {canvas.pixels.map((row, y) =>
          row.map((color, x) => {
            const highlight = highlightMap.get(`${x},${y}`);
            const displayColor = highlight || color;
            const opacity = highlight ? 1.0 : canvasOpacity;
            
            return (
              <rect
                key={`${x}-${y}`}
                x={x * pixelSize}
                y={y * pixelSize}
                width={pixelSize}
                height={pixelSize}
                fill={displayColor}
                fillOpacity={opacity}
                stroke={highlight ? '#000' : undefined}
                strokeWidth={highlight ? 0.5 : 0}
              />
            );
          })
        )}
      </svg>
    </div>
  );
}
